import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Coffee, Trash2 } from 'lucide-react';
import { useAuthStore } from '../../../store/authStore';
import { getSessionId } from '../../../services/api-client';
import {
    updateShopSettings,
    type ShopBean, type BeanMethod, type ShopSpecial, type WeeklyGoal,
} from '../../../services/shop-service';
import { getSquareCatalogItems, type CatalogStampItem } from '../../../services/square-service';
import StampStylePicker from '../StampStylePicker';
import { MARK_PALETTE, shopMarkColor } from '../../shared/PixelLogo';
import LogoUpload from '../LogoUpload';
import { useShopSettingsLoad } from './useShopSettingsLoad';
import OwnerShell, { OwnerLoadingShell } from '../OwnerShell';
import CoralHeader from '../../dashboard/CoralHeader';

const SAVE_CHIP_CLS = 'flex-none rounded-full bg-black/[.18] px-3.5 py-2 font-heading text-[8px] text-white disabled:opacity-50';

// Shared coral form styles.
const INPUT_CLS = 'w-full p-3 bg-background rounded-[8px] border-2 border-hairline focus:border-coral outline-none text-primary text-sm font-grotesk placeholder:text-body-grey';
const LABEL_CLS = 'block font-heading text-[8px] text-body-grey mb-1 uppercase';
const GHOST_PILL_CLS = 'rounded-full bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[12px] px-3 py-2 transition-colors hover:border-coral hover:text-coral disabled:opacity-40 disabled:hover:border-hairline disabled:hover:text-body-grey';
const TRASH_BTN_CLS = 'p-2 text-error hover:text-error/70 shrink-0';

/**
 * First of four owner-settings sub-screens (Task 10 of the ShopSettings
 * split). Sections below are copied VERBATIM from ShopSettings.tsx
 * (SHOP LOGO, STAMP STYLE, STOREFRONT, WHAT WE'RE POURING, WEEKLY SPECIALS)
 * per Nik's explicit ruling — ShopSettings.tsx itself is untouched and stays
 * routable until Task 14 removes it. The five old per-section save buttons
 * are collapsed into one handleSaveAll, driven by the header's SAVE chip
 * (Task 15 removed the body's redundant full-width duplicate, porting its
 * distinct UPLOADING LOGO... state into the chip's label first).
 */
function StorefrontSettings() {
    const navigate = useNavigate();
    const { userAddress } = useAuthStore();
    const { loading, shopId, shopName, shopRow } = useShopSettingsLoad();

    const [stampIcon, setStampIcon] = useState('default');
    const [pixelImageUrl, setPixelImageUrl] = useState<string | undefined>(undefined);
    const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [savingStamp, setSavingStamp] = useState(false);
    const [beans, setBeans] = useState<ShopBean[]>([]);
    const [savingBeans, setSavingBeans] = useState(false);
    const [specials, setSpecials] = useState<ShopSpecial[]>([]);
    const [weeklyGoal, setWeeklyGoal] = useState<WeeklyGoal>({ enabled: false, target: 0, label: '' });
    const [savingGoal, setSavingGoal] = useState(false);
    const [savingSpecials, setSavingSpecials] = useState(false);
    // accentColor null = street-palette default.
    const [accentColor, setAccentColor] = useState<string | null>(null);
    const [currencyName, setCurrencyName] = useState('');
    const [savingStorefront, setSavingStorefront] = useState(false);
    // Weekly Specials' Square item picker (CARRY-FORWARD from Task 10's
    // review): with ShopSettings.tsx deleted in Task 14, the SQUARE STAMP
    // ITEMS section that used to populate this catalog lives on the COUNTER
    // sub-screen now — a separate page, so it can't hand this screen a
    // loaded catalog anymore. This screen loads its own copy on demand via
    // the same LOAD SQUARE MENU affordance CounterSettings uses, so specials
    // can still link Square items without duplicating stamp-eligibility
    // state. null = not loaded yet (distinct from a genuinely empty Square
    // Item Library, which is []).
    const [catalogItems, setCatalogItems] = useState<CatalogStampItem[] | null>(null);
    const [catalogError, setCatalogError] = useState<string | null>(null);
    const [savingAll, setSavingAll] = useState(false);

    async function loadCatalog() {
        setCatalogError(null);
        try {
            const items = await getSquareCatalogItems();
            setCatalogItems(items);
        } catch {
            setCatalogItems(null);
            setCatalogError('COULDN\'T LOAD YOUR SQUARE MENU — TRY AGAIN');
        }
    }

    // Populate section state from the shared loader's row — mirrors the
    // relevant slice of ShopSettings.tsx's loadShopData().
    useEffect(() => {
        if (!shopRow) return;
        setStampIcon(shopRow.stamp_icon || 'default');
        setPixelImageUrl(shopRow.pixel_image_url || undefined);
        setImageUrl(shopRow.image_url || undefined);
        if (Array.isArray(shopRow.beans)) setBeans(shopRow.beans);
        if (Array.isArray(shopRow.specials)) setSpecials(shopRow.specials);
        if (shopRow.weekly_goal) setWeeklyGoal(shopRow.weekly_goal);
        setAccentColor(shopRow.accent_color ?? null);
        setCurrencyName(shopRow.currency_name ?? '');
    }, [shopRow]);

    async function handleUploadLogo() {
        if (!shopId || !logoFile || !userAddress) return;
        setUploadingLogo(true);
        try {
            const formPayload = new FormData();
            formPayload.append('logo', logoFile);
            formPayload.append('owner', userAddress);

            const sessionId = getSessionId();
            const uploadRes = await fetch('/api/shops/upload-logo', {
                method: 'POST',
                headers: sessionId ? { Authorization: `Bearer ${sessionId}` } : undefined,
                body: formPayload,
            });
            if (!uploadRes.ok) {
                const body = await uploadRes.json().catch(() => null);
                throw new Error(body?.error || `Upload failed (${uploadRes.status})`);
            }
            const uploadData = await uploadRes.json();

            const result = await updateShopSettings({
                shopId,
                image_url: uploadData.url,
                pixel_image_url: uploadData.pixelUrl,
            });
            if (!result) throw new Error('Save failed');

            setImageUrl(uploadData.url);
            setPixelImageUrl(uploadData.pixelUrl);
            setLogoFile(null);
        } catch (err) {
            console.error('Failed to upload logo:', err);
            throw err;
        } finally {
            setUploadingLogo(false);
        }
    }

    async function handleSaveStamp() {
        if (!shopId) return;
        setSavingStamp(true);
        try {
            const result = await updateShopSettings({ shopId, stamp_icon: stampIcon });
            if (!result) throw new Error('Save failed');
        } catch (err) {
            console.error('Failed to save stamp style:', err);
            throw err;
        } finally {
            setSavingStamp(false);
        }
    }

    async function handleSaveStorefront() {
        if (!shopId) return;
        const trimmed = currencyName.trim();
        if (trimmed && !/^[A-Za-z0-9][A-Za-z0-9 ]*$/.test(trimmed)) {
            throw new Error('Currency name: letters, numbers and spaces only.');
        }
        if (trimmed && (trimmed.length < 2 || trimmed.length > 12)) {
            throw new Error('Currency name must be 2-12 characters.');
        }
        setSavingStorefront(true);
        try {
            const result = await updateShopSettings({
                shopId,
                accent_color: accentColor,      // null = clear back to street default
                currency_name: trimmed || null, // null = clear back to BEANS
            });
            if (!result) throw new Error('Save failed');
        } catch (err) {
            console.error('Failed to save storefront:', err);
            throw err;
        } finally {
            setSavingStorefront(false);
        }
    }

    function addBean() {
        if (beans.length >= 8) return;
        setBeans([...beans, { method: 'espresso', roaster: '', name: '' }]);
    }
    function removeBean(idx: number) {
        setBeans(beans.filter((_, i) => i !== idx));
    }
    function updateBean(idx: number, field: keyof ShopBean, value: string) {
        setBeans(beans.map((b, i) => (i === idx ? { ...b, [field]: value } : b)));
    }
    async function handleSaveGoal() {
        if (!shopId) return;
        if (weeklyGoal.enabled && !(Number.isInteger(weeklyGoal.target) && weeklyGoal.target > 0)) {
            throw new Error('Set a target of at least 1, or switch the goal off.');
        }
        setSavingGoal(true);
        try {
            const clean: WeeklyGoal = {
                enabled: weeklyGoal.enabled,
                target: weeklyGoal.target,
                label: weeklyGoal.label.trim(),
            };
            const result = await updateShopSettings({ shopId, weekly_goal: clean });
            if (!result) throw new Error('Save failed');
            setWeeklyGoal(clean);
        } finally {
            setSavingGoal(false);
        }
    }

    async function handleSaveBeans() {
        if (!shopId) return;
        setSavingBeans(true);
        try {
            // Drop blank rows before saving.
            const clean = beans.filter(b => b.roaster.trim() && b.name.trim());
            const result = await updateShopSettings({ shopId, beans: clean });
            if (!result) throw new Error('Save failed');
            setBeans(clean);
        } catch (err) {
            console.error('Failed to save beans:', err);
            throw err;
        } finally {
            setSavingBeans(false);
        }
    }

    // Weekly specials (037). valid_until defaults to +7 days; expired
    // entries stop rendering customer-side on their own.
    function addSpecial() {
        if (specials.length >= 6) return;
        setSpecials([...specials, {
            id: `sp_${Date.now().toString(36)}_${specials.length}`,
            title: '', detail: '',
            valid_until: new Date(Date.now() + 7 * 86_400_000).toISOString(),
            bonus_stamps: 0,
        }]);
    }
    function removeSpecial(idx: number) {
        setSpecials(specials.filter((_, i) => i !== idx));
    }
    function updateSpecial(idx: number, patch: Partial<ShopSpecial>) {
        setSpecials(specials.map((sp, i) => (i === idx ? { ...sp, ...patch } : sp)));
    }
    function toggleSpecialItem(idx: number, variationIds: string[], on: boolean) {
        const sp = specials[idx];
        const cur = new Set(sp.square_variation_ids || []);
        for (const v of variationIds) { if (on) cur.add(v); else cur.delete(v); }
        updateSpecial(idx, { square_variation_ids: [...cur] });
    }
    async function handleSaveSpecials() {
        if (!shopId) return;
        setSavingSpecials(true);
        try {
            const clean = specials.filter(sp => sp.title.trim());
            const result = await updateShopSettings({ shopId, specials: clean });
            if (!result) throw new Error('Save failed');
            setSpecials(clean);
        } catch (err) {
            console.error('Failed to save specials:', err);
            throw err;
        } finally {
            setSavingSpecials(false);
        }
    }

    // ONE combined save for the whole STOREFRONT group — sequentially awaits
    // each section's existing save logic (logo upload only when a new file
    // is staged) and reports which section(s) failed rather than bailing on
    // the first error, so one bad section doesn't block the rest.
    async function handleSaveAll() {
        if (!shopId) return;
        setSavingAll(true);
        const failed: string[] = [];
        if (logoFile) {
            try { await handleUploadLogo(); } catch { failed.push('logo'); }
        }
        try { await handleSaveStamp(); } catch { failed.push('stamp style'); }
        try { await handleSaveStorefront(); } catch (err) { failed.push(err instanceof Error ? err.message : 'accent/currency'); }
        try { await handleSaveBeans(); } catch { failed.push('beans'); }
        try { await handleSaveSpecials(); } catch { failed.push('specials'); }
        setSavingAll(false);
        if (failed.length > 0) {
            alert(`Some storefront sections failed to save: ${failed.join(', ')}. Try again.`);
        } else {
            alert('Storefront saved!');
        }
    }

    if (loading) {
        return (
            <OwnerLoadingShell active="setup" statusLabel="SETUP" title="Loading…">
                <div className="space-y-3.5">
                    <div className="bg-white rounded-[10px] h-24 animate-pulse" />
                    <div className="bg-white rounded-[10px] h-64 animate-pulse" />
                </div>
            </OwnerLoadingShell>
        );
    }

    if (!shopId) {
        return (
            <OwnerLoadingShell active="setup" statusLabel="SETUP" title="No shop yet">
                <div className="bg-white rounded-[10px] text-center py-12">
                    <Coffee size={40} className="mx-auto mb-4 text-coral" />
                    <p className="font-heading text-[10px] text-body-grey">NO SHOP REGISTERED YET</p>
                </div>
            </OwnerLoadingShell>
        );
    }

    return (
        <OwnerShell
            active="setup"
            header={
                <CoralHeader
                    statusLabel="SETUP"
                    eyebrow="STOREFRONT SETTINGS"
                    title={shopName}
                    chip={
                        <button
                            onClick={handleSaveAll}
                            disabled={savingAll || uploadingLogo || savingStamp || savingStorefront || savingBeans || savingSpecials}
                            className={SAVE_CHIP_CLS}
                        >
                            {/* Task 15: the body's full-width SAVE STOREFRONT button
                                (with its distinct UPLOADING LOGO... state) was a
                                duplicate of this chip — removed. The chip now carries
                                that same distinct label so the signal isn't lost. */}
                            {savingAll ? (uploadingLogo ? 'UPLOADING…' : 'SAVING…') : 'SAVE'}
                        </button>
                    }
                />
            }
        >
        <div className="space-y-3.5 pb-8">
            {/* sr-only: CoralHeader's title already carries the shop name —
                mockup drops the repeated in-body page title. */}
            <h1 className="sr-only">Storefront</h1>

            <button
                onClick={() => navigate('/shops/settings')}
                className="font-grotesk font-bold text-[13px] text-body-grey hover:text-coral py-2 flex items-center gap-2"
            >
                <ArrowLeft size={13} /> Settings
            </button>

            {/* Shop Logo */}
            <div className="bg-white rounded-[10px] p-[18px]">
                <h2 className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Shop logo</h2>
                <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey mb-3.5">
                    Shown on your shop page. We also make a pixelated 32×32 version
                    you can use as your stamp icon below.
                </p>

                {imageUrl && !logoFile && (
                    <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-[10px] border-[3px] border-primary bg-accent-alt overflow-hidden shrink-0">
                            <img
                                src={pixelImageUrl || imageUrl}
                                alt="Current shop logo"
                                className="w-full h-full object-contain"
                                style={{ imageRendering: 'pixelated' }}
                            />
                        </div>
                        <p className="font-heading text-[8px] text-body-grey">
                            CURRENT LOGO — UPLOAD A NEW ONE TO REPLACE IT
                        </p>
                    </div>
                )}

                <LogoUpload onFileSelected={setLogoFile} currentFile={logoFile} showLabel={false} />
            </div>

            {/* Stamp Style */}
            <div className="bg-white rounded-[10px] p-[18px]">
                <h2 className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Stamp style</h2>
                <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey mb-3.5">
                    The icon that fills a customer's Pixel Pass when you approve a stamp.
                    Pick a preset, or use your pixelated logo.
                </p>
                <StampStylePicker
                    value={stampIcon}
                    hasLogo={!!pixelImageUrl}
                    pixelImageUrl={pixelImageUrl}
                    onChange={setStampIcon}
                />
            </div>

            {/* Storefront (040): accent color + currency name */}
            <div className="bg-white rounded-[10px] p-[18px]">
                <h2 className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Storefront</h2>
                <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey mb-3.5">
                    Make your storefront yours. The accent tints your shop's card and
                    stamps; the currency renames what customers collect (BEANS →
                    SNAGS, CRUMBS...). Both only ever change YOUR storefront.
                </p>

                <label className={LABEL_CLS}>Accent color</label>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                    {MARK_PALETTE.map((c) => (
                        <button
                            key={c}
                            type="button"
                            aria-label={`accent ${c}`}
                            onClick={() => setAccentColor(c)}
                            className="w-9 h-9 rounded-[8px] border-[3px] shrink-0"
                            style={{
                                background: c,
                                borderColor: accentColor === c ? '#111111' : '#e7ded0',
                            }}
                        />
                    ))}
                    <input
                        type="color"
                        aria-label="custom accent color"
                        value={accentColor || shopMarkColor(shopName || '?')}
                        onChange={(e) => setAccentColor(e.target.value)}
                        className="w-9 h-9 rounded-[8px] bg-background border-2 border-hairline cursor-pointer p-0.5"
                        title="Custom color"
                    />
                    <button
                        type="button"
                        onClick={() => setAccentColor(null)}
                        className={GHOST_PILL_CLS}
                        style={{ opacity: accentColor === null ? 0.4 : 1 }}
                    >
                        Street default
                    </button>
                </div>
                <p className="font-heading text-[8px] text-body-grey mb-4">
                    {accentColor
                        ? <>YOUR TINT: <span style={{ color: accentColor }}>{accentColor.toUpperCase()}</span></>
                        : <>USING THE STREET'S PALETTE PICK: <span style={{ color: shopMarkColor(shopName || '?') }}>{shopMarkColor(shopName || '?').toUpperCase()}</span></>}
                </p>

                <label className={LABEL_CLS}>Currency name</label>
                <input
                    type="text"
                    maxLength={12}
                    value={currencyName}
                    onChange={(e) => setCurrencyName(e.target.value)}
                    placeholder="BEANS"
                    className={`${INPUT_CLS} mb-1`}
                />
                <p className="font-heading text-[8px] text-body-grey mt-1">
                    CUSTOMERS COLLECT “{(currencyName.trim() || 'BEANS').toUpperCase()}” AT YOUR SHOP. LEAVE BLANK FOR BEANS.
                </p>
            </div>

            {/* What We're Pouring */}
            <div className="bg-white rounded-[10px] p-[18px]">
                <div className="flex items-center justify-between mb-[5px] flex-wrap gap-3">
                    <h2 className="font-grotesk font-bold text-[17px] text-primary">What we're pouring</h2>
                    <button onClick={addBean} disabled={beans.length >= 8} className={GHOST_PILL_CLS}>
                        + Add bean
                    </button>
                </div>
                <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey mb-3.5">
                    The beans you serve, shown to customers on your shop. Roaster + bean name only —
                    a simple shout-out to the roasters you love.
                </p>

                <div className="space-y-2.5">
                    {beans.map((bean, idx) => (
                        <div key={idx} className="p-3 rounded-[8px] bg-background border-2 border-hairline grid grid-cols-1 md:grid-cols-[7rem_1fr_1fr_auto] gap-2 items-end">
                            <div>
                                <label className={LABEL_CLS}>Method</label>
                                <select value={bean.method}
                                    onChange={e => updateBean(idx, 'method', e.target.value as BeanMethod)}
                                    className={INPUT_CLS}>
                                    <option value="espresso">Espresso</option>
                                    <option value="filter">Filter</option>
                                    <option value="decaf">Decaf</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <div>
                                <label className={LABEL_CLS}>Roaster</label>
                                <input type="text" maxLength={60} value={bean.roaster}
                                    onChange={e => updateBean(idx, 'roaster', e.target.value)}
                                    placeholder="Padre Coffee" className={INPUT_CLS} />
                            </div>
                            <div>
                                <label className={LABEL_CLS}>Bean / blend</label>
                                <input type="text" maxLength={60} value={bean.name}
                                    onChange={e => updateBean(idx, 'name', e.target.value)}
                                    placeholder="The Daily" className={INPUT_CLS} />
                            </div>
                            <button onClick={() => removeBean(idx)} className={TRASH_BTN_CLS} title="Remove bean">
                                <Trash2 size={18} />
                            </button>
                        </div>
                    ))}
                    {beans.length === 0 && (
                        <p className="font-grotesk font-medium text-[13px] text-body-grey text-center py-4">
                            No beans listed yet. Add one to show customers what you pour.
                        </p>
                    )}
                </div>
            </div>

            {/* Weekly goal (060) — sits beside "What we're pouring" on the
                customer's HOME, so it is configured right after it here too. */}
            <div className="bg-white rounded-[10px] p-[18px]">
                <h2 className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Weekly goal</h2>
                <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey mb-3.5">
                    A target your customers can see and help you hit. Resets every
                    Monday morning.
                </p>

                <label className="flex items-center gap-3 mb-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={weeklyGoal.enabled}
                        onChange={(e) => setWeeklyGoal({ ...weeklyGoal, enabled: e.target.checked })}
                        className="w-4 h-4 accent-coral"
                    />
                    <span className="font-grotesk font-bold text-[13px] text-primary">Show a weekly goal</span>
                </label>

                {weeklyGoal.enabled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                        <div>
                            <label className={LABEL_CLS} htmlFor="goal-target">This week's target</label>
                            <input
                                id="goal-target"
                                type="number" min={1} value={weeklyGoal.target || ''}
                                onChange={(e) => setWeeklyGoal({ ...weeklyGoal, target: parseInt(e.target.value, 10) || 0 })}
                                placeholder="200"
                                className={INPUT_CLS}
                            />
                        </div>
                        <div>
                            <label className={LABEL_CLS} htmlFor="goal-label">What you're counting</label>
                            <input
                                id="goal-label"
                                type="text" maxLength={30} value={weeklyGoal.label}
                                onChange={(e) => setWeeklyGoal({ ...weeklyGoal, label: e.target.value })}
                                placeholder="coffees"
                                className={INPUT_CLS}
                            />
                            <p className="font-grotesk font-medium text-[12px] text-body-grey mt-1">
                                The counter tracks every stamp, whatever the drink — the
                                label is just what your customers read.
                            </p>
                        </div>
                    </div>
                )}

                <button
                    onClick={() => { handleSaveGoal().catch((e) => alert(e.message)); }}
                    disabled={savingGoal}
                    className="mt-3.5 rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 px-6 disabled:opacity-50"
                >
                    {savingGoal ? 'Saving…' : 'Save goal'}
                </button>
            </div>

            {/* Weekly Specials */}
            <div className="bg-white rounded-[10px] p-[18px]">
                <div className="flex items-center justify-between mb-[5px] flex-wrap gap-3">
                    <h2 className="font-grotesk font-bold text-[17px] text-primary">Weekly specials</h2>
                    <button onClick={addSpecial} disabled={specials.length >= 6} className={GHOST_PILL_CLS}>
                        + Add special
                    </button>
                </div>
                <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey mb-3.5">
                    Shown on your card in every customer's app — advertising they chose to carry.
                    Specials expire automatically. Link a Square item and set a bonus to reward
                    customers for buying it.
                </p>
                <div className="space-y-2.5">
                    {specials.map((sp, idx) => (
                        <div key={sp.id} className="p-3 rounded-[8px] bg-background border-2 border-hairline space-y-2">
                            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                                <div>
                                    <label className={LABEL_CLS}>Special</label>
                                    <input type="text" maxLength={80} value={sp.title}
                                        onChange={e => updateSpecial(idx, { title: e.target.value })}
                                        placeholder="Blueberry muffins $4.50" className={INPUT_CLS} />
                                </div>
                                <div>
                                    <label className={LABEL_CLS}>Detail (optional)</label>
                                    <input type="text" maxLength={140} value={sp.detail || ''}
                                        onChange={e => updateSpecial(idx, { detail: e.target.value })}
                                        placeholder="While stocks last" className={INPUT_CLS} />
                                </div>
                                <button onClick={() => removeSpecial(idx)} className={TRASH_BTN_CLS} title="Remove special">
                                    <Trash2 size={18} />
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-4 items-end">
                                <div>
                                    <label className={LABEL_CLS}>Runs until</label>
                                    <input type="date"
                                        value={sp.valid_until ? sp.valid_until.slice(0, 10) : ''}
                                        onChange={e => updateSpecial(idx, { valid_until: e.target.value ? new Date(`${e.target.value}T23:59:59`).toISOString() : sp.valid_until })}
                                        className={INPUT_CLS} />
                                </div>
                                <div>
                                    <label className={LABEL_CLS}>Bonus stamps for buying</label>
                                    <select value={sp.bonus_stamps || 0}
                                        onChange={e => updateSpecial(idx, { bonus_stamps: Number(e.target.value) })}
                                        className={INPUT_CLS}>
                                        <option value={0}>None</option>
                                        <option value={1}>+1 stamp</option>
                                        <option value={2}>+2 stamps</option>
                                    </select>
                                </div>
                            </div>
                            {(sp.bonus_stamps || 0) > 0 && (
                                <div>
                                    <label className="block font-grotesk font-medium text-[13px] text-body-grey mb-1">
                                        Which Square item is the special? (bonus fires when it's bought)
                                    </label>
                                    {catalogItems === null ? (
                                        <div>
                                            <button onClick={loadCatalog} className={GHOST_PILL_CLS}>Load Square menu</button>
                                            {catalogError && (
                                                <p className="font-grotesk font-medium text-[12px] text-error mt-3">{catalogError}</p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="max-h-36 overflow-y-auto rounded-[8px] border-2 border-hairline p-2 space-y-1">
                                            {catalogItems.map((item) => (
                                                <label key={item.id} className="flex items-center gap-2 font-grotesk font-medium text-xs text-primary cursor-pointer">
                                                    <input type="checkbox" className="accent-coral" checked={item.variationIds.some(v => (sp.square_variation_ids || []).includes(v))}
                                                        onChange={e => toggleSpecialItem(idx, item.variationIds, e.target.checked)} />
                                                    {item.name}{item.category ? ` · ${item.category}` : ''}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                    {specials.length === 0 && (
                        <p className="font-grotesk font-medium text-[13px] text-body-grey text-center py-4">
                            No specials running. Add one — it shows on your card in every customer's app.
                        </p>
                    )}
                </div>
            </div>
        </div>
        </OwnerShell>
    );
}

export default StorefrontSettings;
